import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { toast } from 'react-toastify'
import {
  fetchScreenerDomains,
  fetchScreenerTargetSpecialties,
  saveScreenerProfile,
} from 'src/supportPortal/services/screenerProfileAdminService'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const PERMISSION_FIELDS = [
  { key: 'canSubmitFinal', label: 'Submit final' },
  { key: 'canFlagEmergency', label: 'Flag emergency' },
  { key: 'canOverrideTriage', label: 'Override triage' },
  { key: 'canAssignDoctor', label: 'Assign doctor' },
  { key: 'canViewPastScreenings', label: 'View past screenings' },
  { key: 'canEditAfterSubmit', label: 'Edit after submit' },
]

const DEFAULT_PERMISSIONS = PERMISSION_FIELDS.reduce((acc, field) => {
  acc[field.key] = false
  return acc
}, {})

function normalizeSlugOption(item) {
  if (!item) return null
  const slug = item.slug || item.id || item._id || item.title || item.name
  if (!slug) return null
  return { slug, label: item.title || item.name || item.label || slug }
}

function mapSlugsToOptions(slugs = [], options = []) {
  return (slugs || [])
    .filter(Boolean)
    .map((slug) => options.find((option) => option.slug === slug) || { slug, label: slug })
}

function splitTextValues(value) {
  if (!value) return []
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function getScreenerServiceDetails(practitioner) {
  return practitioner?.teacherUnEditableFields?.servicesApproved?.ROLE_SCREENER || {}
}

function buildInitialForm(practitioner, profile) {
  const serviceDetails = getScreenerServiceDetails(practitioner)
  return {
    supportedTargetSpecialtySlugs:
      profile?.supportedTargetSpecialtySlugs || serviceDetails?.targetSpecialitySlugs || [],
    supportedDomainSlugs: profile?.supportedDomainSlugs || [],
    clinicIdsText: (profile?.clinicIds || []).join(', '),
    enabledProtocolsText: (profile?.enabledProtocols || []).join(', '),
    rostering: Boolean(profile?.rostering),
    payoutsEnabled: Boolean(profile?.payoutsEnabled),
    enabled: profile?.enabled !== false,
    permissions: { ...DEFAULT_PERMISSIONS, ...(profile?.permissions || {}) },
  }
}

/**
 * Create / edit a Screener Profile for a single practitioner, used by the "All Screeners (by role)"
 * tab. Mirrors the fields of ScreenerProfileAdminPanel's editor but scoped to one row. The save
 * endpoint (and this dialog) require servicesApproved.ROLE_SCREENER to be enabled — the caller
 * only opens it for such rows — and is gated to ROLE_SCREENER_ADMIN server-side.
 */
export default function ScreenerProfileCreateDialog({ open, onClose, practitioner, onSaved }) {
  const [targetSpecialties, setTargetSpecialties] = useState([])
  const [domains, setDomains] = useState([])
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const practitionerId = practitioner?.id
  const isEditing = Boolean(practitioner?.hasScreenerProfile)
  const displayName = practitioner?.name || practitioner?.firstName || 'Screener'

  const targetSpecialtyOptions = useMemo(
    () => targetSpecialties.map(normalizeSlugOption).filter(Boolean),
    [targetSpecialties],
  )
  const domainOptions = useMemo(() => domains.map(normalizeSlugOption).filter(Boolean), [domains])

  useEffect(() => {
    if (!open || !practitionerId) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      setForm(null)
      try {
        const [targetList, domainList] = await Promise.all([
          fetchScreenerTargetSpecialties(),
          fetchScreenerDomains(),
        ])
        let existingProfile = null
        if (isEditing) {
          const resp = await getSPDataWithParams(
            '/api/secure/cc/support/screeners/profiles/by-mentor-id',
            { mentorId: practitionerId, includeDisabled: true, includeDeleted: false },
          )
          if (resp?.status === 200) {
            const raw = getDataMapFromCommonResponse(resp) || resp?.data?.data || resp?.data
            existingProfile = raw?.screenerProfile ?? raw?.profile ?? raw ?? null
          }
        }
        if (cancelled) return
        setTargetSpecialties(targetList)
        setDomains(domainList)
        setForm(buildInitialForm(practitioner, existingProfile))
      } catch (err) {
        if (!cancelled) setError('Unable to load screener profile data.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, practitionerId])

  function updateFormValue(key, value) {
    setForm((current) => ({ ...(current || {}), [key]: value }))
  }

  function updatePermission(key, value) {
    setForm((current) => ({
      ...(current || {}),
      permissions: { ...(current?.permissions || DEFAULT_PERMISSIONS), [key]: value },
    }))
  }

  async function handleSave() {
    if (!practitionerId || !form) return
    setSaving(true)
    try {
      const response = await saveScreenerProfile({
        practitionerId,
        supportedDomainSlugs: form.supportedDomainSlugs || [],
        supportedTargetSpecialtySlugs: form.supportedTargetSpecialtySlugs || [],
        clinicIds: splitTextValues(form.clinicIdsText),
        enabledProtocols: splitTextValues(form.enabledProtocolsText),
        rostering: Boolean(form.rostering),
        payoutsEnabled: Boolean(form.payoutsEnabled),
        enabled: Boolean(form.enabled),
        permissions: form.permissions || DEFAULT_PERMISSIONS,
      })
      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const saved = getDataMapFromCommonResponse(response)?.['screener-profile']
        toast.success(isEditing ? 'Screener profile updated' : 'Screener profile created')
        onSaved?.(saved || { practitionerId, enabled: Boolean(form.enabled) })
        onClose?.()
      } else {
        const message = response?.data?.message
        toast.error(typeof message === 'string' ? message : 'Unable to save screener profile')
      }
    } catch (err) {
      toast.error('Unable to save screener profile')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEditing ? 'Edit Screener Profile' : 'Create Screener Profile'}
        <Typography variant="body2" color="text.secondary">
          {displayName}
          {practitioner?.email ? ` · ${practitioner.email}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading || !form ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.enabled)}
                    onChange={(event) => updateFormValue('enabled', event.target.checked)}
                  />
                }
                label="Enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.rostering)}
                    onChange={(event) => updateFormValue('rostering', event.target.checked)}
                  />
                }
                label="Rostering"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.payoutsEnabled)}
                    onChange={(event) => updateFormValue('payoutsEnabled', event.target.checked)}
                  />
                }
                label="Payouts section"
              />
            </Stack>

            <Autocomplete
              multiple
              options={targetSpecialtyOptions}
              value={mapSlugsToOptions(form.supportedTargetSpecialtySlugs, targetSpecialtyOptions)}
              getOptionLabel={(option) => option?.label || option?.slug || ''}
              isOptionEqualToValue={(option, value) => option.slug === value.slug}
              onChange={(_, value) =>
                updateFormValue('supportedTargetSpecialtySlugs', value.map((item) => item.slug))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return <Chip key={key || option.slug} size="small" label={option.label} {...tagProps} />
                })
              }
              renderInput={(params) => <TextField {...params} label="Target specialties" />}
            />

            <Autocomplete
              multiple
              options={domainOptions}
              value={mapSlugsToOptions(form.supportedDomainSlugs, domainOptions)}
              getOptionLabel={(option) => option?.label || option?.slug || ''}
              isOptionEqualToValue={(option, value) => option.slug === value.slug}
              onChange={(_, value) =>
                updateFormValue('supportedDomainSlugs', value.map((item) => item.slug))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return <Chip key={key || option.slug} size="small" label={option.label} {...tagProps} />
                })
              }
              renderInput={(params) => <TextField {...params} label="Screening domains" />}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Clinic IDs"
                  helperText="Comma-separated"
                  value={form.clinicIdsText}
                  onChange={(event) => updateFormValue('clinicIdsText', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Enabled protocols"
                  helperText="Comma-separated, e.g. PHQ9, GAD7"
                  value={form.enabledProtocolsText}
                  onChange={(event) => updateFormValue('enabledProtocolsText', event.target.value)}
                />
              </Grid>
            </Grid>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Permissions
              </Typography>
              <FormGroup row>
                {PERMISSION_FIELDS.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={Boolean(form.permissions?.[field.key])}
                        onChange={(event) => updatePermission(field.key, event.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                ))}
              </FormGroup>
            </Box>
            <Divider />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || !form}
        >
          {isEditing ? 'Save Profile' : 'Create Profile'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
